import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import { useNotification } from '../../sharedComponents/Notifications';
import Pagination from '../components/Pagination';
import { Modal, MultiSelect } from '../components/adminModals';
import Drawer from '../../sharedComponents/Drawer';
import {
    primaFacieReportService,
    primaFacieFinalDecisionService,
    submissionsByDeskService,
    categoryAdminService,
    PrimaFacieProcessItem,
    PrimaFacieAssignmentSummary,
    PrimaFacieSuggestedReviewerItem,
    PrimaFacieFinalDecisionDetail,
    DeskItem,
    Category,
    userService,
    CategoryMemberDropdownItem,
    primaFacieService,
    SubmissionStatus,
    submissionService,
    isAuthorMoveDecision,
    statusForAuthorMoveDecision,
} from '../services/adminAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../auth/hooks/auth';
import { Eye, CheckCircle, Clock, XCircle, AlertTriangle, MessageSquare, ChevronDown, PlusCircle, Wifi, WifiOff, SendHorizontal, X, ArrowRightLeft } from 'lucide-react';
import { communicationService, InternalMessage } from '../services/communicationService';
import signalRService, { ConnectionStatus } from '../../../services/signalRService';
import AdminSubmissionDetails from '../components/AdminSubmissionDetails';

// ── Constants ─────────────────────────────────────────────────────────────────

const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

// ─────────────────────────────────────────────────────────────────────────────

function PrimaFacieReports() {
    const { showNotification } = useNotification();
    const { user, refreshUnread } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const isCategoryAdmin = user?.role === 'thematicarealeader';
    const isDESSOrTagChairman = user?.roleId === 9 || user?.roleId === 5 || user?.role === 'dess';

    // Chat Drawer State
    const [selectedSubmission, setSelectedSubmission] = useState<PrimaFacieProcessItem | null>(null);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState<InternalMessage[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState<string>('');
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [additionalDetails, setAdditionalDetails] = useState<string>('');
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(ConnectionStatus.Disconnected);
    const [unreadSubmissionIds, setUnreadSubmissionIds] = useState<Set<string>>(new Set());

    // Lookups for Chat
    const [roles, setRoles] = useState<{ id: string; name: string }[]>([]);
    const [roleUsers, setRoleUsers] = useState<{ id: string; name: string }[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // ── Category resolution ───────────────────────────────────────────────────
    const [categories, setCategories] = useState<Category[]>([]);
    const [categoryAdminCategoryId, setCategoryAdminCategoryId] = useState<number | null>(null);
    const [initialized, setInitialized] = useState(false);

    // ── Table state ───────────────────────────────────────────────────────────
    const [submissions, setSubmissions] = useState<PrimaFacieProcessItem[]>([]);
    const [loading, setLoading] = useState(true);

    // ── Pagination ────────────────────────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // ── Filters ───────────────────────────────────────────────────────────────
    const [search, setSearch] = useState('');
    const [completedFilter, setCompletedFilter] = useState<boolean>(isCategoryAdmin ? false : true);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number>(0);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // ── Change Submission Status (DESS only) ──────────────────────────────────
    const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
    const [statusTarget, setStatusTarget] = useState<PrimaFacieProcessItem | null>(null);
    const [newStatus, setNewStatus] = useState<number | ''>('');
    const [statusComment, setStatusComment] = useState('');
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

    // ── Final Decision Modal state ────────────────────────────────────────────
    const [isFinalDecisionOpen, setIsFinalDecisionOpen] = useState(false);
    const [finalDecisionTarget, setFinalDecisionTarget] = useState<PrimaFacieProcessItem | null>(null);

    // Form fields
    const [fdDecision, setFdDecision] = useState<'' | '0' | '1'>('');
    const [fdAllowResubmission, setFdAllowResubmission] = useState<'' | 'true' | 'false'>('');
    const [fdComment, setFdComment] = useState('');
    const [fdToWorkflowStage, setFdToWorkflowStage] = useState<number | ''>('');
    const [fdSelectedReviewerIds, setFdSelectedReviewerIds] = useState<(string | number)[]>([]);

    // Desk list
    const [deskList, setDeskList] = useState<DeskItem[]>([]);
    const [deskListLoading, setDeskListLoading] = useState(false);

    // Suggested reviewers from API
    const [suggestedReviewers, setSuggestedReviewers] = useState<PrimaFacieSuggestedReviewerItem[]>([]);
    const [fetchingReviewers, setFetchingReviewers] = useState(false);

    // Submitting
    const [isSubmittingFinalDecision, setIsSubmittingFinalDecision] = useState(false);

    // ── View Final Decision Drawer state ──────────────────────────────────────
    const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false);
    const [viewDecisionData, setViewDecisionData] = useState<PrimaFacieFinalDecisionDetail | null>(null);
    const [loadingDecision, setLoadingDecision] = useState(false);

    // ── Add Panel Member modal ─────────────────────────────────────────────────
    const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
    const [addPanelTarget, setAddPanelTarget] = useState<PrimaFacieProcessItem | null>(null);
    const [addPanelMembers, setAddPanelMembers] = useState<CategoryMemberDropdownItem[]>([]);
    const [fetchingAddMembers, setFetchingAddMembers] = useState(false);
    const [selectedAddMemberIds, setSelectedAddMemberIds] = useState<(string | number)[]>([]);
    const [isAddingPanel, setIsAddingPanel] = useState(false);

    // ── Delete Panel Member modal ──────────────────────────────────────────────
    const [isDeletePanelOpen, setIsDeletePanelOpen] = useState(false);
    const [deletePanelTarget, setDeletePanelTarget] = useState<PrimaFacieProcessItem | null>(null);
    const [selectedDeleteAssignmentId, setSelectedDeleteAssignmentId] = useState('');
    const [isDeletingMember, setIsDeletingMember] = useState(false);

    const [isSubmissionDetailsOpen, setIsSubmissionDetailsOpen] = useState(false);
    const [detailsSubmissionId, setDetailsSubmissionId] = useState<string | null>(null);

    // ── Bootstrap ─────────────────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            try {
                const res = await categoryAdminService.getCategoriesForCategoryAdmin();
                if (res.isSuccess && res.data) {
                    setCategories(res.data);
                    if (isCategoryAdmin) {
                        if (res.data.length > 0) {
                            const id = res.data[0].id;
                            setCategoryAdminCategoryId(id);
                            setSelectedCategoryId(id);
                        } else {
                            setCategoryAdminCategoryId(0);
                            setLoading(false);
                        }
                    } else {
                        setCategoryAdminCategoryId(0);
                    }
                } else {
                    if (isCategoryAdmin) { setCategoryAdminCategoryId(0); setLoading(false); }
                }
            } catch {
                if (isCategoryAdmin) { setCategoryAdminCategoryId(0); setLoading(false); }
            } finally {
                setInitialized(true);
            }
        };
        init();
    }, []);

    // ── Chat Functions & SignalR Integration ───────────────────────────────────

    // SignalR state listener for local UI
    useEffect(() => {
        const unsubState = signalRService.onStateChange((status) => {
            setConnectionStatus(status);
        });

        // Join group when chat opens for a specific submission
        if (isChatOpen && selectedSubmission?.submissionId && connectionStatus === ConnectionStatus.Connected) {
            signalRService.joinGroup(selectedSubmission.submissionId);
        }

        const unsubscribe = signalRService.onReceiveMessage((data: any) => {
            const msgSubId = data.submissionId || data.SubmissionId;
            const msgId = data.id || data.Id;

            const newMsg: InternalMessage = {
                id: msgId || Date.now().toString(),
                message: data.message || data.Message,
                senderName: data.senderName || data.SenderName || 'System',
                senderUserId: data.senderUserId || data.SenderUserId,
                sendDateTime: data.sendDateTime || data.SendDateTime || new Date().toISOString(),
                receiverName: data.receiverName || data.ReceiverName
            };

            // If chat is open for this submission, append immediately
            if (isChatOpen && selectedSubmission && msgSubId === selectedSubmission.submissionId) {
                setChatMessages(prev => {
                    // Avoid duplicates if the message was already added optimistically
                    if (prev.some(m => m.id === newMsg.id)) return prev;
                    return [...prev, newMsg];
                });
            } else if (msgSubId) {
                // Otherwise mark it as unread (green dot)
                setUnreadSubmissionIds(prev => new Set(prev).add(msgSubId));
            }
        });

        return () => {
            // Leave group when chat closes or submission changes
            if (selectedSubmission?.submissionId && connectionStatus === ConnectionStatus.Connected) {
                signalRService.leaveGroup(selectedSubmission.submissionId);
            }
            unsubscribe();
            unsubState();
        };
    }, [isChatOpen, selectedSubmission?.submissionId, connectionStatus]);

    // Fetch filtered list of roles
    const fetchRoles = async () => {
        try {
            const rolesRes = await userService.getAdminRoles();
            if (rolesRes.isSuccess && rolesRes.data) {
                const mappedRoles = rolesRes.data.map(r => ({ id: r.id.toString(), name: r.name }));
                
                // Logged-in user role checks
                const isDESS = user?.roleId === 9 || user?.role === 'dess';
                const isTagChairman = user?.roleId === 5;
                
                let filtered = mappedRoles;
                if (isDESS) {
                    // DESS only sees TAG Chairman
                    filtered = mappedRoles.filter(r => r.id === '5');
                } else if (isTagChairman) {
                    // TAG Chairman only sees DESS
                    filtered = mappedRoles.filter(r => r.id === '9');
                } else {
                    // Fallback or other roles
                    filtered = mappedRoles.filter(r => r.id === '9' || r.id === '5');
                }
                
                setRoles(filtered);
            }
        } catch (err) {
            }
    };

    const fetchUsersByRole = async (roleId: string) => {
        setLoadingUsers(true);
        try {
            const res = await userService.getUsersByRoleId(Number(roleId));
            if (res.isSuccess && res.data) {
                setRoleUsers(res.data.map(u => ({
                    id: u.userId,
                    name: u.person?.fullName || u.username
                })));
            }
        } catch (err) {
            } finally {
            setLoadingUsers(false);
        }
    };

    const fetchChatHistory = async (submissionId: string) => {
        setLoadingHistory(true);
        try {
            const res = await communicationService.getMessageHistory(submissionId);
            if (res.isSuccess && res.data) {
                const sortedMessages = [...res.data].sort((a, b) =>
                    new Date(a.sendDateTime).getTime() - new Date(b.sendDateTime).getTime()
                );
                setChatMessages(sortedMessages);
            }
        } catch (err) {
            } finally {
            setLoadingHistory(false);
        }
    };

    const handleOpenChat = async (submission: PrimaFacieProcessItem) => {
        if (connectionStatus !== ConnectionStatus.Connected) {
            showNotification('error', {
                title: 'Offline',
                message: 'You cannot open the chat interface while offline. Please check your internet connection or wait for the system to reconnect.'
            });
            return;
        }

        setSelectedSubmission(submission);
        setIsChatOpen(true);
        // Clear unread dot for this submission
        setUnreadSubmissionIds(prev => {
            const next = new Set(prev);
            next.delete(submission.submissionId);
            return next;
        });

        // Sync read status with server
        try {
            await communicationService.markAsRead(submission.submissionId);
            if (refreshUnread) await refreshUnread();
        } catch (err) {
            }

        fetchChatHistory(submission.submissionId);
    };

    const handleCloseChat = () => {
        setIsChatOpen(false);
        setChatMessages([]);
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedUsers.length === 0 || !selectedSubmission || !additionalDetails.trim()) return;

        setSendingMessage(true);
        try {
            const payload = {
                submissionId: selectedSubmission.submissionId,
                receiverId: selectedUsers.join(','),
                message: additionalDetails.trim()
            };
            const res = await communicationService.sendMessage(payload);
            
            if (res.isSuccess) {
                setIsModalOpen(false);
                setAdditionalDetails('');
                setSelectedUsers([]);
                setSelectedRoleId('');
                showNotification('success', { title: 'Message Sent', message: 'Your message has been delivered.' });
            }
        } catch (err) {
            showNotification('error', { title: 'Send Failed', message: 'Could not deliver your message.' });
        } finally {
            setSendingMessage(false);
        }
    };

    // Handle deep link to chat from global notification
    useEffect(() => {
        if (!initialized || submissions.length === 0) return;

        const params = new URLSearchParams(location.search);
        const openChatId = params.get('openChat');

        if (openChatId) {
            const targetSub = submissions.find(s => s.submissionId === openChatId);
            if (targetSub) {
                handleOpenChat(targetSub);
                // Clear the param from URL
                window.history.replaceState({}, '', location.pathname);
            }
        }
    }, [location.search, submissions, initialized]);

    // Fetch roles on load if user is DESS or TAG Chairman
    useEffect(() => {
        if (isDESSOrTagChairman) {
            fetchRoles();
        }
    }, [isDESSOrTagChairman]);

    // ── Fetch on filter / page change ─────────────────────────────────────────
    useEffect(() => {
        if (!initialized) return;
        if (isCategoryAdmin && categoryAdminCategoryId === null) return;
        if (isCategoryAdmin && categoryAdminCategoryId === 0) return;

        const handler = setTimeout(() => { fetchSubmissions(); }, 400);
        return () => clearTimeout(handler);
    }, [initialized, currentPage, pageSize, search, completedFilter, selectedCategoryId, categoryAdminCategoryId]);

    const fetchSubmissions = async () => {
        setLoading(true);
        try {
            const response = await primaFacieReportService.getSubmissionsUnderPrimaFacieProcess({
                pageNumber: currentPage,
                pageSize,
                sortBy: '',
                sortDescending: true,
                search,
                completed: isCategoryAdmin ? completedFilter : true,
                categoryId: isCategoryAdmin ? (categoryAdminCategoryId ?? 0) : selectedCategoryId,
            });

            if (response.isSuccess && response.data) {
                setSubmissions(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch submissions' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while fetching submissions' });
            } finally {
            setLoading(false);
        }
    };

    // ── Open final decision modal ─────────────────────────────────────────────
    const openFinalDecisionModal = async (row: PrimaFacieProcessItem) => {
        const assignments = row.assignments ?? [];
        // Strict 3-member panel rule (kept for reference, temporarily relaxed — at least 1 required):
        // if (assignments.length < 3) {
        //     showNotification('error', {
        //         title: 'Insufficient Panel Members',
        //         message: 'A minimum of 3 panel members is required to make a final decision. Please add more panel members before proceeding.',
        //     });
        //     return;
        // }
        if (assignments.length < 1) {
            showNotification('error', {
                title: 'No Panel Members Assigned',
                message: 'At least 1 panel member must be assigned to make a final decision. Please assign a panel member before proceeding.',
            });
            return;
        }

        const hasIncomplete = assignments.some(a => a.status?.toLowerCase() !== 'completed');
        if (hasIncomplete) {
            showNotification('error', {
                title: 'Cannot Make Final Decision',
                message: 'All panel evaluations must be completed before making a final decision. Please wait for pending evaluations to be submitted.',
            });
            return;
        }
        setFinalDecisionTarget(row);
        setFdDecision('');
        setFdAllowResubmission('');
        setFdComment('');
        setFdToWorkflowStage('');
        setFdSelectedReviewerIds([]);
        setIsFinalDecisionOpen(true);

        // Fetch desk list and suggested reviewers in parallel
        setDeskListLoading(true);
        setFetchingReviewers(true);

        try {
            const [deskRes, reviewerRes] = await Promise.all([
                submissionsByDeskService.getDeskList(),
                primaFacieFinalDecisionService.getSuggestedReviewersBySubmissionId(row.submissionId),
            ]);

            setDeskList(deskRes.isSuccess && deskRes.data ? deskRes.data : []);

            if (reviewerRes.isSuccess && reviewerRes.data) {
                setSuggestedReviewers(reviewerRes.data);
            } else {
                setSuggestedReviewers([]);
                showNotification('error', { title: 'Error', message: reviewerRes.message || 'Failed to load suggested reviewers' });
            }
        } catch {
            setDeskList([]);
            setSuggestedReviewers([]);
            showNotification('error', { title: 'Error', message: 'Failed to load modal data' });
        } finally {
            setDeskListLoading(false);
            setFetchingReviewers(false);
        }
    };

    const closeFinalDecisionModal = () => {
        if (isSubmittingFinalDecision) return;
        setIsFinalDecisionOpen(false);
        setFinalDecisionTarget(null);
        setFdDecision('');
        setFdAllowResubmission('');
        setFdComment('');
        setFdToWorkflowStage('');
        setFdSelectedReviewerIds([]);
        setSuggestedReviewers([]);
        setDeskList([]);
    };

    // ── Open view final decision drawer ─────────────────────────────────────
    const openViewDecisionDrawer = async (row: PrimaFacieProcessItem) => {
        setIsViewDrawerOpen(true);
        setViewDecisionData(null);
        setLoadingDecision(true);
        try {
            const res = await primaFacieFinalDecisionService.getFinalDecisionBySubmissionId(row.submissionId);
            if (res?.isSuccess === true && res.data) {
                setViewDecisionData(res.data);
            } else {
                showNotification('error', { title: 'Error', message: res?.message || 'Failed to load final decision details' });
                setIsViewDrawerOpen(false);
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
            setIsViewDrawerOpen(false);
        } finally {
            setLoadingDecision(false);
        }
    };

    const handleSubmitFinalDecision = async () => {
        if (!finalDecisionTarget) return;
        if (!fdDecision) {
            showNotification('error', { title: 'Required', message: 'Please select a recommendation.' });
            return;
        }
        if (fdToWorkflowStage === '') {
            showNotification('error', { title: 'Required', message: 'Please select the next desk to move to.' });
            return;
        }
        // Suggested reviewers are only required when the decision is Recommended (not '1' = Not Recommended)
        if (fdDecision !== '1' && (fdSelectedReviewerIds.length < 1 || fdSelectedReviewerIds.length > 3)) {
            showNotification('error', { title: 'Required', message: 'Please select between 1 and 3 reviewers.' });
            return;
        }
        if (fdDecision === '1' && !fdAllowResubmission) {
            showNotification('error', { title: 'Required', message: 'Please specify if resubmission is allowed.' });
            return;
        }

        setIsSubmittingFinalDecision(true);
        await new Promise(r => setTimeout(r, 0));

        try {
            // Don't send suggested reviewers when the decision is Not Recommended ('1')
            const resolvedReviewers = fdDecision === '1'
                ? []
                : fdSelectedReviewerIds
                    .map(id => suggestedReviewers.find(r => r.id === id))
                    .filter(Boolean) as PrimaFacieSuggestedReviewerItem[];

            const res = await primaFacieFinalDecisionService.submitFinalDecision({
                submissionId: finalDecisionTarget.submissionId,
                decision: parseInt(fdDecision, 10),
                comment: fdComment,
                toWorkflowStage: fdToWorkflowStage as number,
                allowResubmission: fdDecision === '1' ? fdAllowResubmission === 'true' : false,
                recommendedReviewers: resolvedReviewers.map(r => ({
                    id: ZERO_GUID,
                    primaFacieFinalDecisionId: ZERO_GUID,
                    name: r.name,
                    email: r.email,
                    phoneNo: r.phoneNo,
                    affiliateInstitution: r.affiliateInstitution,
                })),
            });

            if (res?.isSuccess === true) {
                showNotification('success', { title: 'Decision Submitted', message: 'The final Prima Facie decision has been submitted successfully.' });
                closeFinalDecisionModal();
                fetchSubmissions();
            } else {
                showNotification('error', { title: 'Submission Failed', message: res?.message || 'Failed to submit final decision.' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred.' });
        } finally {
            setIsSubmittingFinalDecision(false);
        }
    };

    const openAddPanelModal = async (row: PrimaFacieProcessItem) => {
        setAddPanelTarget(row);
        setSelectedAddMemberIds([]);
        setIsAddPanelOpen(true);
        setFetchingAddMembers(true);
        try {
            const response = await primaFacieService.getCategoryMemberDropdown(row.categoryId);
            if (response.isSuccess && response.data) {
                const existingIds = new Set(row.assignments.map(a => a.memberId));
                setAddPanelMembers(response.data.filter(m => !existingIds.has(m.id)));
            } else {
                setAddPanelMembers([]);
                showNotification('error', { title: 'Error', message: response.message || 'Failed to load members' });
            }
        } catch {
            setAddPanelMembers([]);
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
        } finally {
            setFetchingAddMembers(false);
        }
    };

    const closeAddPanelModal = () => {
        setIsAddPanelOpen(false);
        setAddPanelTarget(null);
        setSelectedAddMemberIds([]);
        setAddPanelMembers([]);
    };

    const handleAddPanelMembers = async () => {
        if (!addPanelTarget || selectedAddMemberIds.length === 0) return;
        setIsAddingPanel(true);
        try {
            const assignedMembers = selectedAddMemberIds.map(id => {
                const member = addPanelMembers.find(m => m.id === id);
                return { memberId: id.toString(), name: member?.name ?? '' };
            });
            const response = await primaFacieService.assignMembersForPrimaFacie({
                submissionId: addPanelTarget.submissionId,
                assignedMembers,
            });
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Panel member(s) added successfully' });
                closeAddPanelModal();
                fetchSubmissions();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to add members' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
        } finally {
            setIsAddingPanel(false);
        }
    };

    const openDeletePanelModal = (row: PrimaFacieProcessItem) => {
        setDeletePanelTarget(row);
        setSelectedDeleteAssignmentId('');
        setIsDeletePanelOpen(true);
    };

    const closeDeletePanelModal = () => {
        if (isDeletingMember) return;
        setIsDeletePanelOpen(false);
        setDeletePanelTarget(null);
        setSelectedDeleteAssignmentId('');
    };

    const handleDeletePanelMember = async () => {
        if (!selectedDeleteAssignmentId) {
            showNotification('error', { title: 'Required', message: 'Please select a member to remove.' });
            return;
        }
        setIsDeletingMember(true);
        try {
            const response = await primaFacieFinalDecisionService.deletePrimaFacieAssignment(selectedDeleteAssignmentId);
            if (response?.isSuccess) {
                showNotification('success', { title: 'Removed', message: 'Panel member removed successfully.' });
                closeDeletePanelModal();
                fetchSubmissions();
            } else {
                showNotification('error', { title: 'Error', message: response?.message || 'Failed to remove member.' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred.' });
        } finally {
            setIsDeletingMember(false);
        }
    };

    const handleViewSubmissionDetails = (submissionId: string) => {
        setDetailsSubmissionId(submissionId);
        setIsSubmissionDetailsOpen(true);
    };

    // ── Filter handlers ───────────────────────────────────────────────────────
    const handleSearch = (query: string) => { setSearch(query); setCurrentPage(1); };
    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePageSizeChange = (size: number) => { setPageSize(size); setCurrentPage(1); };
    const handleClearFilters = () => {
        setSearch('');
        setCompletedFilter(isCategoryAdmin ? false : true);
        if (!isCategoryAdmin) setSelectedCategoryId(0);
        setCurrentPage(1);
    };

    const handleViewPrimaFacie = (assignmentId: string) => {
        navigate(`/admin/prima-facie/${assignmentId}`);
    };

    // ── Helpers ───────────────────────────────────────────────────────────────
    const getApplicationTypeLabel = (type: string) => {
        const lower = type?.toLowerCase();
        if (lower === 'book' || lower === '1') return 'Book';
        if (lower === 'journalarticle' || lower === '2') return 'Journal Article';
        return type || 'N/A';
    };

    const getStatusChip = (status: string) => {
        const lower = status?.toLowerCase();
        if (lower === 'completed' || lower === 'submitted') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-green-50 text-green-700">
                    <CheckCircle className="w-3 h-3" />{status}
                </span>
            );
        }
        if (lower === 'pending' || lower === 'notstarted') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700">
                    <Clock className="w-3 h-3" />Pending
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600">
                <XCircle className="w-3 h-3" />{status}
            </span>
        );
    };

    // ── Table columns ─────────────────────────────────────────────────────────
    const columns: TableColumn<PrimaFacieProcessItem>[] = [
        {
            key: 'submissionNumber',
            label: 'ID',
            render: (value) => <span className="text-gray-500 text-sm font-mono">{value}</span>,
        },
        {
            key: 'title',
            label: 'Title / Lead Author',
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900 mb-0.5 leading-snug">{row.title}</div>
                    <div className="text-xs text-gray-500">{row.fullName}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{getApplicationTypeLabel(row.applicationType)}</div>
                </div>
            ),
        },
        {
            key: 'categoryName',
            label: 'Thematic Area',
            render: (value) => (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                    {value || 'N/A'}
                </span>
            ),
        },
        {
            key: 'completedPrimaFacie',
            label: 'Final Decision Made?',
            render: (value, row) => value ? (
                <div className="space-y-2 flex flex-col">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold text-green-700">
                        <CheckCircle className="w-3.5 h-3.5" />Yes
                    </span>
                    {row.decision === 'NotRecommended' ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-red-50 text-red-700">
                            <XCircle className="w-3.5 h-3.5" />Not Recommended
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700">
                            <CheckCircle className="w-3.5 h-3.5" />Recommended
                        </span>
                    )}
                    {row.decision === 'NotRecommended' && (
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${
                            row.allowResubmission
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-gray-100 text-gray-500'
                        }`}>
                            {row.allowResubmission ? <>Resubmission allowed</> : <>No resubmission</>}
                        </span>
                    )}
                </div>
            ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-700">
                    <Clock className="w-3.5 h-3.5" />No
                </span>
            ),
        },
        {
            key: 'assignments',
            label: 'Assigned Prima Facie Panel',
            render: (value, row) => {
                const assignments: PrimaFacieAssignmentSummary[] = row.assignments ?? [];
                if (assignments.length === 0) {
                    return <span className="text-xs text-gray-400 italic">No panel assigned</span>;
                }
                return (
                    <div className="space-y-1.5">
                        {assignments.map((a, idx) => (
                            <div key={a.primaFacieAssignmentId} className="flex items-center gap-2 min-h-[28px]">
                                <div className="w-5 h-5 border rounded-full bg-gray-100 flex items-center justify-center text-gray-900 font-bold text-[10px] flex-shrink-0">
                                    {idx + 1}
                                </div>
                                <span className="text-xs text-gray-700 font-medium flex-1 min-w-0 truncate">
                                    {a.memberName}
                                </span>
                                {getStatusChip(a.status)}
                                <div className="w-6 flex-shrink-0 flex justify-end">
                                    {a.status?.toLowerCase() === 'completed' && a.primaFacieReviewId && (
                                        <button
                                            onClick={() => handleViewPrimaFacie(a.primaFacieAssignmentId)}
                                            title="View Prima Facie review"
                                            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-[#088A61] transition-colors"
                                        >
                                            <Eye className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            },
        },
    ];

    // ── Change Status handlers (DESS only) ────────────────────────────────────
    const openChangeStatusModal = (row: PrimaFacieProcessItem) => {
        setStatusTarget(row);
        setNewStatus('');
        setStatusComment('');
        setIsChangeStatusModalOpen(true);
    };

    const closeChangeStatusModal = () => {
        setIsChangeStatusModalOpen(false);
        setStatusTarget(null);
        setNewStatus('');
        setStatusComment('');
    };

    const handleChangeStatus = async () => {
        if (!statusTarget || newStatus === '') return;
        setIsUpdatingStatus(true);
        try {
            const payload = {
                submissionId: statusTarget.submissionId,
                comment: statusComment.trim() || '',
                submissionStatus: newStatus,
            };
            const response = await submissionService.moveSubmissionToAuthor(payload as any);
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: response.message || 'Submission moved to author successfully.' });
                closeChangeStatusModal();
                fetchSubmissions();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to move submission to author.' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred while updating status.' });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    // ── Actions — single dropdown with conditional className per item ─────────
    // className as a function returning 'hidden' hides the item when not applicable.
    const actions: TableAction<PrimaFacieProcessItem>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                {
                    label: 'View Submission Details',
                    onClick: (row: PrimaFacieProcessItem) => handleViewSubmissionDetails(row.submissionId),
                    className: 'text-blue-600',
                },
                {
                    label: 'Make Final Decision',
                    onClick: (row: PrimaFacieProcessItem) => openFinalDecisionModal(row),
                    className: (row: PrimaFacieProcessItem) =>
                        !isCategoryAdmin || row.completedPrimaFacie ? 'hidden' : 'text-blue-600',
                },
                {
                    label: 'View Final Decision',
                    onClick: (row: PrimaFacieProcessItem) => openViewDecisionDrawer(row),
                    className: (row: PrimaFacieProcessItem) =>
                        !row.completedPrimaFacie ? 'hidden' : 'text-blue-600',
                },
                // ── NEW ──
                {
                    label: 'Add Panel Member',
                    onClick: (row: PrimaFacieProcessItem) => openAddPanelModal(row),
                    className: (row: PrimaFacieProcessItem) =>
                        row.completedPrimaFacie ? 'hidden' : 'text-green-600',
                },
                {
                    label: 'Delete Panel Member',
                    onClick: (row: PrimaFacieProcessItem) => openDeletePanelModal(row),
                    className: (row: PrimaFacieProcessItem) => {
                        const hasRemovable = row.assignments.some(
                            a => a.status?.toLowerCase() === 'not started'
                        );
                        return row.completedPrimaFacie || !hasRemovable ? 'hidden' : 'text-red-600';
                    },
                },
                {
                    label: 'Discuss (Internal)',
                    onClick: (row: PrimaFacieProcessItem) => handleOpenChat(row),
                    className: (row: PrimaFacieProcessItem) =>
                        !row.completedPrimaFacie || !isDESSOrTagChairman ? 'hidden' : 'text-green-600 font-semibold',
                },
                // Change Submission Status — DESS or TAG Chairman, gated by final decision
                {
                    label: 'Move submission to Author',
                    onClick: (row: PrimaFacieProcessItem) => openChangeStatusModal(row),
                    className: (row: PrimaFacieProcessItem) =>
                        isDESSOrTagChairman && isAuthorMoveDecision(row.decision)
                            ? 'text-[#088A61] font-semibold'
                            : 'hidden',
                },
            ],
            title: 'Actions',
        },
    ];

    // ── Category dropdown for filter ──────────────────────────────────────────
    const renderCategoryDropdown = () => {
        if (isCategoryAdmin) {
            if (categoryAdminCategoryId === 0 || categories.length === 0) {
                return (
                    <select disabled className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-400 cursor-not-allowed">
                        <option>No Thematic Area</option>
                    </select>
                );
            }
            return (
                <select disabled value={categories[0].id} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                    <option value={categories[0].id}>{categories[0].name}</option>
                </select>
            );
        }
        return (
            <select
                value={selectedCategoryId}
                onChange={(e) => { setSelectedCategoryId(parseInt(e.target.value)); setCurrentPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                <option value={0}>All Thematic Areas</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
        );
    };

    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Thematic Area</label>
                    {renderCategoryDropdown()}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Review Status</label>
                    {isCategoryAdmin ? (
                        <select
                            value={completedFilter ? 'true' : 'false'}
                            onChange={(e) => { setCompletedFilter(e.target.value === 'true'); setCurrentPage(1); }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="false">In Progress</option>
                            <option value="true">Completed</option>
                        </select>
                    ) : (
                        <select disabled className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                            <option value="true">Completed</option>
                        </select>
                    )}
                </div>
            </div>
            <button onClick={handleClearFilters} className="mt-4 text-sm text-blue-600 hover:underline">
                Clear filters
            </button>
        </div>
    );

    const emptyMessage = isCategoryAdmin && categoryAdminCategoryId === 0
        ? 'No Thematic Area assigned to this admin yet'
        : 'No submissions found under Prima Facie process.';

    const Spinner = () => (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="w-full">
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Prima Facie Reports</h1>
                <p className="text-gray-500 text-sm sm:text-base">
                    Track submissions currently under the Prima Facie review process.
                </p>
            </div>

            <ReusableTable
                title="Prima Facie Process"
                subTitle="Submissions and their Prima Facie panel assignments"
                columns={columns}
                data={submissions}
                actions={actions}
                loading={loading}
                searchPlaceholder="Search by title, author, or ID..."
                onSearch={handleSearch}
                emptyMessage={emptyMessage}
                showSearch={true}
                showFilter={true}
                filterPanel={filterPanel}
                onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
                isFilterOpen={isFilterOpen}
            />

            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            )}

            {/* ── Make Final Decision Modal ─────────────────────────────────── */}
            <Modal
                isOpen={isFinalDecisionOpen}
                onClose={closeFinalDecisionModal}
                title="Make Final Prima Facie Decision"
                description="This decision is final and cannot be undone. Review carefully before submitting."
                maxWidth="lg"
            >
                <div className="space-y-3 mt-1">

                    {/* Submission pill */}
                    {finalDecisionTarget && (
                        <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-semibold text-gray-900 line-clamp-1">{finalDecisionTarget.title}</p>
                            <p className="text-xs text-gray-400">{finalDecisionTarget.submissionNumber} · {finalDecisionTarget.categoryName}</p>
                        </div>
                    )}

                    {/* Warning banner — compact */}
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                        <p className="text-xs text-amber-700 font-medium">
                            This is <strong>irreversible</strong>. Ensure all panel reviews have been considered.
                        </p>
                    </div>

                    {/* Row 1: Recommendation + Move to Desk side by side */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Recommendation <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={fdDecision}
                                onChange={e => {
                                    setFdDecision(e.target.value as '' | '0' | '1');
                                    setFdAllowResubmission('');
                                }}
                                className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent"
                            >
                                <option value="">Select…</option>
                                <option value="0">Recommended</option>
                                <option value="1">Not Recommended</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Move to Next Desk <span className="text-red-500">*</span>
                            </label>
                            {deskListLoading ? (
                                <div className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                    <Spinner />Loading…
                                </div>
                            ) : (
                                <select
                                    value={fdToWorkflowStage}
                                    onChange={e => setFdToWorkflowStage(e.target.value === '' ? '' : Number(e.target.value))}
                                    className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent"
                                >
                                    <option value="">Select desk…</option>
                                    {deskList.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            )}
                        </div>
                    </div>

                    {/* Allow resubmission — only when Not Recommended */}
                    {fdDecision === '1' && (
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Allow Resubmission? <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={fdAllowResubmission}
                                onChange={e => setFdAllowResubmission(e.target.value as '' | 'true' | 'false')}
                                className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent"
                            >
                                <option value="">Select…</option>
                                <option value="true">Yes — allow resubmission</option>
                                <option value="false">No — do not allow resubmission</option>
                            </select>
                        </div>
                    )}

                    {/* Comment */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={fdComment}
                            onChange={e => setFdComment(e.target.value)}
                            placeholder="Add any notes about this decision…"
                            rows={2}
                            className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent resize-none"
                        />
                    </div>

                    {/* Reviewers — MultiSelect — hidden when Not Recommended ('1') */}
                    {fdDecision !== '1' && (
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Reviewers from Suggested Panel <span className="text-red-500">*</span>
                            <span className="text-gray-400 font-normal ml-1">(select 1 to 3)</span>
                        </label>
                        {fetchingReviewers ? (
                            <div className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                <Spinner />Loading reviewers…
                            </div>
                        ) : suggestedReviewers.length === 0 ? (
                            <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 text-center italic">
                                No suggested reviewers found
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                                {suggestedReviewers.map(r => {
                                    const isSelected = fdSelectedReviewerIds.includes(r.id);
                                    const atLimit = fdSelectedReviewerIds.length >= 3 && !isSelected;
                                    return (
                                    <label
                                        key={r.id}
                                        className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                                        isSelected
                                            ? 'bg-emerald-50 border-emerald-200'
                                            : atLimit
                                            ? 'bg-gray-50 border-gray-100 opacity-50 cursor-not-allowed'
                                            : 'bg-gray-50 border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/40'
                                        }`}
                                    >
                                        <input
                                        type="checkbox"
                                        className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#088A61] focus:ring-[#088A61]"
                                        checked={isSelected}
                                        disabled={atLimit}
                                        onChange={() => {
                                            if (isSelected) {
                                            setFdSelectedReviewerIds(prev => prev.filter(id => id !== r.id));
                                            } else if (!atLimit) {
                                            setFdSelectedReviewerIds(prev => [...prev, r.id]);
                                            }
                                        }}
                                        />
                                        <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-gray-800">{r.name}</p>
                                        {r.affiliateInstitution && (
                                            <p className="text-xs text-[#088A61] font-medium mt-0.5">{r.affiliateInstitution}</p>
                                        )}
                                        <p className="text-xs text-gray-400 mt-0.5">{r.email}</p>
                                        {r.phoneNo && <p className="text-xs text-gray-400">{r.phoneNo}</p>}
                                        </div>
                                        {isSelected && (
                                        <CheckCircle className="w-4 h-4 text-[#088A61] flex-shrink-0 mt-0.5" />
                                        )}
                                    </label>
                                    );
                                })}
                            </div>
                            // <MultiSelect
                            //     options={suggestedReviewers.map(r => ({
                            //         value: r.id,
                            //         label: `${r.name} — ${r.email}`,
                            //     }))}
                            //     selectedValues={fdSelectedReviewerIds}
                            //     onChange={(vals) => {
                            //         if (vals.length <= 3) setFdSelectedReviewerIds(vals);
                            //     }}
                            //     placeholder="Select 1–3 reviewers…"
                            // />
                        )}
                        {fdSelectedReviewerIds.length > 0 && (
                            <p className={`text-xs mt-1 font-medium ${fdSelectedReviewerIds.length >= 1 ? 'text-green-700' : 'text-amber-600'}`}>
                                {fdSelectedReviewerIds.length} selected ✓
                            </p>
                        )}
                    </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                        <button
                            onClick={closeFinalDecisionModal}
                            disabled={isSubmittingFinalDecision}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmitFinalDecision}
                            disabled={isSubmittingFinalDecision}
                            className="px-5 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isSubmittingFinalDecision ? <><Spinner />Submitting…</> : 'Submit Final Decision'}
                        </button>
                    </div>
                </div>
            </Modal>
            {/* ── View Final Decision Drawer ────────────────────────────────── */}
            <Drawer
                isOpen={isViewDrawerOpen}
                onClose={() => { setIsViewDrawerOpen(false); setViewDecisionData(null); }}
                title="Final Prima Facie Decision"
                width="max-w-2xl"
            >
                {loadingDecision ? (
                    <div className="flex items-center justify-center h-48 gap-3 text-gray-400">
                        <Spinner />
                        <span className="text-sm">Loading decision details…</span>
                    </div>
                ) : viewDecisionData ? (() => {
                    const d = viewDecisionData;
                    const sub = d.submission;
                    const authorsDisplay = (sub?.authors && String(sub.authors).trim() !== '')
                        ? sub.authors
                        : ((sub as any)?.bookAuthors && (sub as any).bookAuthors.length > 0
                            ? (sub as any).bookAuthors.map((a: any) => a?.name ?? '').filter(Boolean).join(', ')
                            : null);
                    const isRecommended = d.decision?.toLowerCase() === 'recommended';

                    const formatDate = (s: string) => s
                        ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })
                        : '—';

                    return (
                        <div className="space-y-0 divide-y divide-gray-100">

                            {/* ── Decision badge + title ── */}
                            <div className="pb-5 space-y-3">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold ${
                                        isRecommended
                                            ? 'bg-green-50 text-green-700'
                                            : 'bg-red-50 text-red-700'
                                    }`}>
                                        {isRecommended
                                            ? <CheckCircle className="w-3.5 h-3.5" />
                                            : <XCircle className="w-3.5 h-3.5" />
                                        }
                                        {isRecommended ? 'Recommended' : 'Not Recommended'}
                                    </span>
                                    {d.allowResubmission && !isRecommended && (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-700">
                                            Resubmission Allowed
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900 leading-snug">{sub?.title || '—'}</h2>
                                    <p className="text-xs text-gray-400 mt-1 font-mono">{sub?.submissionNumber}</p>
                                </div>
                            </div>

                            {/* ── Submission quick stats ── */}
                            <div className="py-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Type</p>
                                    <p className="text-sm font-medium text-gray-800">
                                        {sub?.applicationType === 1 ? 'Book' : sub?.applicationType === 2 ? 'Journal Article' : '—'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Thematic Area</p>
                                    <p className="text-sm font-medium text-gray-800">{sub?.category?.name || '—'}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Submitted</p>
                                    <p className="text-sm font-medium text-gray-800">{formatDate(sub?.submittedAt || '')}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Similarity</p>
                                    <p className={`text-sm font-bold ${
                                        (sub?.plagiarismOutcome ?? 0) >= 25 ? 'text-red-600'
                                        : (sub?.plagiarismOutcome ?? 0) >= 15 ? 'text-yellow-600'
                                        : 'text-green-600'
                                    }`}>
                                        {sub?.plagiarismOutcome ?? 0}%
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Decision Date</p>
                                    <p className="text-sm font-medium text-gray-800">{formatDate(d.dateCreated)}</p>
                                </div>
                            </div>

                            {/* ── Author ── */}
                            <div className="py-5">
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Author</h3>
                                {sub?.author?.person ? (
                                    <div className="flex items-start gap-3">
                                        <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">
                                            {sub.author.person.firstName?.[0]}{sub.author.person.lastName?.[0]}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-gray-900 text-sm">{sub.author.person.fullName}</p>
                                            <p className="text-xs text-gray-500">{sub.author.person.email}</p>
                                            {sub.author.person.institution?.name && (
                                                <p className="text-xs text-gray-400 mt-0.5">{sub.author.person.institution.name}</p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-400 italic">Author information unavailable</p>
                                )}
                                {authorsDisplay && (
                                    <div className="mt-3 pt-3 border-t border-gray-50">
                                        <p className="text-xs text-gray-400 mb-1">All listed authors</p>
                                        <p className="text-sm text-gray-700">{authorsDisplay}</p>
                                    </div>
                                )}
                            </div>

                            {/* ── Decided by ── */}
                            <div className="py-5">
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Decision Made By</h3>
                                {d.decidedBy?.person ? (
                                    <div className="flex items-start gap-3">
                                        <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm flex-shrink-0">
                                            {d.decidedBy.person.firstName?.[0]}{d.decidedBy.person.lastName?.[0]}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-gray-900 text-sm">{d.decidedBy.person.fullName}</p>
                                            <p className="text-xs text-gray-500">{d.decidedBy.person.email}</p>
                                            {d.decidedBy.person.institution?.name && (
                                                <p className="text-xs text-gray-400 mt-0.5">{d.decidedBy.person.institution.name}</p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-400 italic">Decider information unavailable</p>
                                )}
                            </div>

                            {/* ── Final Decision Details ── */}
                            <div className="py-5 space-y-4">
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Final Decision Details</h3>

                                {/* Decision + resubmission row */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className={`rounded-lg p-3 border ${isRecommended ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-gray-500">Decision</p>
                                        <p className={`text-sm font-bold ${isRecommended ? 'text-green-700' : 'text-red-700'}`}>
                                            {isRecommended ? 'Recommended' : 'Not Recommended'}
                                        </p>
                                    </div>
                                    {!isRecommended && (
                                        <div className={`rounded-lg p-3 border ${d.allowResubmission ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100'}`}>
                                            <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-gray-500">Resubmission</p>
                                            <p className={`text-sm font-bold ${d.allowResubmission ? 'text-amber-700' : 'text-gray-600'}`}>
                                                {d.allowResubmission ? 'Allowed' : 'Not Allowed'}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Comment */}
                                {d.comment && (
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Comment</p>
                                        <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                                            {d.comment}
                                        </p>
                                    </div>
                                )}

                                {/* Recommended reviewers */}
                                {d.primaFacieFinalRecommendedReviewers?.length > 0 && (
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">
                                            Recommended Reviewers ({d.primaFacieFinalRecommendedReviewers.length})
                                        </p>
                                        <div className="space-y-2">
                                            {d.primaFacieFinalRecommendedReviewers.map((r, idx) => (
                                                <div key={r.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-100">
                                                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-[10px] flex-shrink-0">
                                                        {idx + 1}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium text-gray-800">{r.name}</p>
                                                        {r.affiliateInstitution && (
                                                            <p className="text-xs text-[#088A61] font-medium mt-0.5">{r.affiliateInstitution}</p>
                                                        )}
                                                        <p className="text-xs text-gray-500">{r.email}</p>
                                                        {r.phoneNo && <p className="text-xs text-gray-400">{r.phoneNo}</p>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>
                    );
                })() : null}
            </Drawer>

            {/* ── Add Panel Member Modal ────────────────────────────────────────────── */}
            <Modal
                isOpen={isAddPanelOpen}
                onClose={closeAddPanelModal}
                title="Add Panel Member"
                description="Select one or more thematic area members to add to the Prima Facie panel."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {addPanelTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{addPanelTarget.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{addPanelTarget.categoryName}</p>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Members to Add <span className="text-red-500">*</span>
                        </label>
                        {fetchingAddMembers ? (
                            <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                <Spinner />Loading members…
                            </div>
                        ) : addPanelMembers.length === 0 ? (
                            <div className="w-full px-3 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 text-center italic">
                                No additional members available for this thematic area
                            </div>
                        ) : (
                            <MultiSelect
                                options={addPanelMembers.map(m => ({ value: m.id, label: m.name }))}
                                selectedValues={selectedAddMemberIds}
                                onChange={setSelectedAddMemberIds}
                                placeholder="Select members to add..."
                            />
                        )}
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button onClick={closeAddPanelModal} disabled={isAddingPanel}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Cancel
                        </button>
                        <button onClick={handleAddPanelMembers} disabled={isAddingPanel || selectedAddMemberIds.length === 0}
                            className="px-5 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                            {isAddingPanel ? <><Spinner />Adding…</> : 'Add Member(s)'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Delete Panel Member Modal ─────────────────────────────────────────── */}
            <Modal
                isOpen={isDeletePanelOpen}
                onClose={closeDeletePanelModal}
                title="Remove Panel Member"
                description="Only members who have not yet started their assignment can be removed."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {deletePanelTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{deletePanelTarget.title}</p>
                        </div>
                    )}
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                        <p className="text-xs text-red-700 font-medium">This will permanently remove the member from this Prima Facie panel.</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Select Member to Remove <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedDeleteAssignmentId}
                            onChange={e => setSelectedDeleteAssignmentId(e.target.value)}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        >
                            <option value="">Select member…</option>
                            {deletePanelTarget?.assignments
                                .filter(a => a.status?.toLowerCase() === 'not started')
                                .map(a => (
                                    <option key={a.primaFacieAssignmentId} value={a.primaFacieAssignmentId}>
                                        {a.memberName}
                                    </option>
                                ))
                            }
                        </select>
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button onClick={closeDeletePanelModal} disabled={isDeletingMember}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Cancel
                        </button>
                        <button onClick={handleDeletePanelMember} disabled={isDeletingMember || !selectedDeleteAssignmentId}
                            className="px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                            {isDeletingMember ? <><Spinner />Removing…</> : 'Remove Member'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Internal Discussion Chat Drawer ── */}
            <Drawer
                isOpen={isChatOpen}
                onClose={handleCloseChat}
                title={`Discussion: ${selectedSubmission?.title}`}
                width="max-w-xl"
            >
                <div className="flex flex-col h-full -m-6">

                    {/* Submission context header — clearly separate from chat */}
                    <div className="bg-gradient-to-r from-[#088A61]/10 to-emerald-50 border-b border-[#088A61]/20 px-5 py-3.5">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-[10px] font-bold text-[#088A61] uppercase tracking-widest mb-0.5">Submission</p>
                                <p className="text-sm font-bold text-gray-900 line-clamp-1">{selectedSubmission?.title}</p>
                                <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{selectedSubmission?.fullName}</p>
                            </div>
                            <div className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${connectionStatus === ConnectionStatus.Connected
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : connectionStatus === ConnectionStatus.Reconnecting
                                    ? 'bg-yellow-50 text-yellow-700 border-yellow-200 animate-pulse'
                                    : 'bg-red-50 text-red-700 border-red-200 animate-pulse'
                                }`}>
                                {connectionStatus === ConnectionStatus.Connected ? <Wifi size={10} /> : <WifiOff size={10} />}
                                {connectionStatus === ConnectionStatus.Connected ? ''
                                    : connectionStatus === ConnectionStatus.Reconnecting ? 'Reconnecting'
                                        : 'Connecting'}
                            </div>
                        </div>
                    </div>

                    {/* Chat messages */}
                    <div className="flex-1 overflow-y-auto px-4 py-5 space-y-3 bg-gray-50">
                        {loadingHistory ? (
                            <div className="flex flex-col items-center justify-center h-full space-y-3">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#088A61]"></div>
                                <p className="text-sm text-gray-500 font-medium">Loading messages...</p>
                            </div>
                        ) : chatMessages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-center p-8 space-y-3">
                                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                                    <MessageSquare size={28} className="text-gray-300" />
                                </div>
                                <p className="text-sm font-semibold text-gray-400">No messages yet</p>
                                <p className="text-xs text-gray-400">Start a discussion using the button below.</p>
                            </div>
                        ) : (
                            chatMessages.map((msg) => {
                                const myName = user?.name?.toLowerCase().trim() || '';
                                const senderName = msg.senderName?.toLowerCase().trim() || '';
                                const isMine = (msg.senderUserId && msg.senderUserId === user?.id) ||
                                    (senderName === myName && myName !== '');
                                return (
                                    <div key={msg.id} className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                                        {/* Sender label */}
                                        <div className={`flex items-center gap-1.5 mb-1 px-1 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0 ${isMine ? 'bg-gray-400' : 'bg-gray-400'
                                                }`}>
                                                {msg.senderName?.charAt(0)?.toUpperCase()}
                                            </div>
                                            <span className="text-[11px] font-semibold text-gray-600">
                                                {isMine ? "" : msg.senderName}
                                            </span>
                                            <span className="text-[10px] text-gray-400">
                                                {new Date(msg.sendDateTime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                            </span>
                                        </div>

                                        {/* Bubble */}
                                        <div className={`max-w-[80%] px-4 py-3 rounded-2xl shadow-sm text-sm leading-relaxed whitespace-pre-wrap ${isMine
                                            ? 'bg-[#088A61] text-white rounded-tr-sm'
                                            : 'bg-white text-gray-800 border border-gray-100 rounded-tl-sm'
                                            }`}>
                                            {msg.message}
                                        </div>

                                        {/* To: label */}
                                        {msg.receiverName && (
                                            <div className="mt-1 px-1 flex items-center gap-1">
                                                <span className={`text-[10px] font-medium ${isMine ? 'text-[#088A61]/70' : 'text-gray-400'
                                                    }`}>
                                                    To: {msg.receiverName}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Compose footer */}
                    <div className="p-4 bg-white border-t border-gray-100 flex justify-center">
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="flex items-center gap-2 bg-[#088A61] text-white px-7 py-3 rounded-full text-sm font-bold shadow-lg shadow-[#088A61]/20 hover:bg-[#077a55] hover:scale-105 active:scale-95 transition-all"
                        >
                            <PlusCircle size={18} />
                            New Message
                        </button>
                    </div>
                </div>
            </Drawer>

            {/* New Discussion Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg relative z-10 overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <h3 className="text-lg font-bold text-gray-900 truncate pr-4">
                                Internal Discussion: <span className="text-[#088A61]">{selectedSubmission?.title}</span>
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Select Role:</label>
                                    <select
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-[#088A61] outline-none"
                                        value={selectedRoleId}
                                        onChange={(e) => {
                                            const roleId = e.target.value;
                                            setSelectedRoleId(roleId);
                                            setSelectedUsers([]);
                                            if (roleId) fetchUsersByRole(roleId);
                                            else setRoleUsers([]);
                                        }}
                                    >
                                        <option value="">Select a Role</option>
                                        {roles.map(role => (
                                            <option key={role.id} value={role.id}>{role.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {selectedRoleId && (
                                    <div className="relative">
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Select User(s):</label>
                                        <div
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm cursor-pointer flex justify-between items-center"
                                            onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                                        >
                                            <span className="truncate text-gray-700 font-medium">
                                                {loadingUsers ? 'Loading...' : selectedUsers.length === 0 ? "Select Recipient" : roleUsers.find(u => u.id === selectedUsers[0])?.name || "User Selected"}
                                            </span>
                                            <ChevronDown size={16} className={`text-gray-400 transition-transform ${isUserDropdownOpen ? 'rotate-180' : ''}`} />
                                        </div>

                                        {isUserDropdownOpen && (
                                            <>
                                                <div className="fixed inset-0 z-10" onClick={() => setIsUserDropdownOpen(false)}></div>
                                                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl z-20 max-h-60 overflow-y-auto p-2">
                                                    {roleUsers.map(user => (
                                                        <label key={user.id} className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer rounded-xl mb-1 transition-colors ${selectedUsers.includes(user.id) ? 'bg-[#088A61]/5 border border-[#088A61]/20' : ''}`}>
                                                            <input
                                                                type="radio" name="chatUser" className="w-4 h-4 border-gray-300 text-[#088A61] focus:ring-[#088A61]"
                                                                checked={selectedUsers.includes(user.id)}
                                                                onChange={() => {
                                                                    setSelectedUsers([user.id]);
                                                                    setIsUserDropdownOpen(false);
                                                                }}
                                                            />
                                                            <span className={`text-sm ${selectedUsers.includes(user.id) ? 'font-bold text-[#088A61]' : 'text-gray-700 font-medium'}`}>{user.name}</span>
                                                        </label>
                                                    ))}
                                                    {roleUsers.length === 0 && !loadingUsers && <p className="p-3 text-xs text-gray-400 italic text-center">No users found</p>}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Compose Message:</label>
                                <textarea
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm focus:ring-2 focus:ring-[#088A61] outline-none min-h-[150px] resize-none"
                                    placeholder="Type your internal message here..."
                                    value={additionalDetails}
                                    onChange={(e) => setAdditionalDetails(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                            <button onClick={() => setIsModalOpen(false)} className="px-6 py-2.5 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-200">Cancel</button>
                            <button
                                onClick={handleSendMessage}
                                disabled={selectedUsers.length === 0 || sendingMessage || !additionalDetails.trim()}
                                className={`px-8 py-2.5 rounded-xl text-sm font-bold shadow-lg transition-all flex items-center gap-2 ${selectedUsers.length > 0 && !sendingMessage && additionalDetails.trim()
                                    ? 'bg-[#088A61] text-white hover:scale-105 active:scale-95'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                            >
                                {sendingMessage ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <SendHorizontal size={18} />
                                )}
                                {sendingMessage ? 'Sending...' : 'Send Message'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Change Submission Status Modal (DESS only) ── */}
            <Modal
                isOpen={isChangeStatusModalOpen}
                onClose={closeChangeStatusModal}
                title="Communicate Submission Status"
                description="Move the submission back to the author with an optional comment."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {statusTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">
                                {statusTarget.title ?? '—'}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5 font-mono">
                                {statusTarget.submissionNumber ?? '—'}
                            </p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Submission Status <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={newStatus}
                            onChange={(e) => setNewStatus(e.target.value === '' ? '' : Number(e.target.value))}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                        >
                            <option value="">Select Status...</option>
                            {(() => {
                                const dec = statusTarget?.decision;
                                if (!isAuthorMoveDecision(dec)) return null;
                                const opt = statusForAuthorMoveDecision(dec);
                                return <option value={opt.value}>{opt.label}</option>;
                            })()}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={statusComment}
                            onChange={(e) => setStatusComment(e.target.value)}
                            placeholder="Add a comment regarding this status change..."
                            rows={4}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none"
                        />
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button
                            onClick={closeChangeStatusModal}
                            disabled={isUpdatingStatus}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleChangeStatus}
                            disabled={newStatus === '' || isUpdatingStatus}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isUpdatingStatus ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Moving...
                                </>
                            ) : (
                                <>
                                    <ArrowRightLeft className="w-4 h-4" />
                                    Move
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            <Drawer
                isOpen={isSubmissionDetailsOpen}
                onClose={() => { setIsSubmissionDetailsOpen(false); setDetailsSubmissionId(null); }}
                title="Submission Details"
                width="max-w-3xl"
            >
                {detailsSubmissionId && (
                    <AdminSubmissionDetails submissionId={detailsSubmissionId} />
                )}
            </Drawer>
        </div>
    );
}

export default PrimaFacieReports;